import './Payment.css'
import ApartmentsApi from '../../services/apartments'
import {
    Box,
    Grid,
    Typography,
    CircularProgress,
    ListItem,
} from '@material-ui/core'
import ApplicationsApi from '../../services/applications'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Suggest from '../Apartment/Stepper/Steps/Suggest'
import { useHistory } from 'react-router-dom'
import Button from '@material-ui/core/Button'
import { useParams } from 'react-router-dom'
import UsersApi from '../../services/users'

type PaymentPopupProps = {
    productTags: string[]
    headertext: string
    infotext: React.ReactNode
    apartment: any
    application?: any
    paymentProviderWidth?: string
    contract?: any
}

type RouteParams = {
    apartmentId: string
    userId: string
}

export default function PaymentPopup({ productTags, infotext, headertext, apartment, application, paymentProviderWidth, contract }: PaymentPopupProps) {
    const { t } = useTranslation()
    const [products, setProducts] = useState<any[]>([])
    const [paymentProviders, setPaymentProviders] = useState<any>(null)
    const [isLoading, setIsLoading] = useState(false)
    const [showPaymentMethods, setShowPaymentMethods] = useState(false)
    const [totalPriceInEuros, setTotalPriceInEuros] = useState(0)
    const history = useHistory()
    const [couponCode, setCouponCode] = useState('')
    const [couponErrorMessage, setCouponErrorMessage] = useState<string | null>(null)
    const [couponSuccessMessage, setCouponSuccessMessage] = useState<string | null>(null)
    const [couponApplied, setCouponApplied] = useState(false)
    const { apartmentId, userId } = useParams<RouteParams>()

    useEffect(() => {
        window.history.pushState(null, '', window.location.href)
        const handleBackButton = () => {
            window.location.reload()
        }
        window.addEventListener('popstate', handleBackButton)
        return () => {
            window.removeEventListener('popstate', handleBackButton)
        }
    }, [history])

    const getProducts = async () => {
        setIsLoading(true)
        try {
            const response = await ApartmentsApi.getProducts(productTags)
            const list = response?.products || []
            setProducts(list)
            const sum = list.reduce((acc: number, p: any) => acc + (p.priceInEuros || 0), 0)
            setTotalPriceInEuros(sum)
            if (list.length > 0) await paymentHandler(Math.round(sum * 100), list)
        } catch (e) {
            console.error('Error fetching products', e)
        } finally {
            setIsLoading(false)
        }
    }

    async function paymentHandler(priceInCents: number, selectedList: any[]) {
        try {
            setIsLoading(true)
            const selectedProducts = selectedList.map(p => p.productId)
            if (application) selectedProducts.push({ applicationCode: application.code })
            const apartmentInfo = {
                userId: apartment.userId,
                apartmentId: apartment.apartmentId,
                apartmentCode: apartment.code,
                publishCostInCents: priceInCents,
                selectedProducts,
                ...(contract && { contract: contract })
            }
            const paymentResponse = await ApplicationsApi.initiatePaymentForPublish(apartmentInfo)
            const filteredResponse = {
                ...paymentResponse,
                providers: paymentResponse.providers?.filter((method: any) => method.id !== 'amex' && method.id !== 'apple-pay' && method.id !== 'nordea-business' && method.id !== 'siirto') || []
            }
            setShowPaymentMethods(true)
            setPaymentProviders(filteredResponse || null)
        } catch (error) {
            console.error(error)
        } finally {
            setIsLoading(false)
        }
    }

    async function handleCoupon(code: string, useCase: string) {
        setIsLoading(true)
        setCouponErrorMessage(null)
        setCouponSuccessMessage(null)
        if (code.length < 4) {
            setCouponErrorMessage(t('paymentPage.couponCodeTooShort'))
            setIsLoading(false)
            return
        }
        try {
            const couponResponse = await ApplicationsApi.handlePaymentCoupon(code, userId, useCase)
            if (couponResponse && couponResponse.status === 'valid') {
                const discount = couponResponse.discountPercentage / 100
                const newPrice = totalPriceInEuros * (1 - discount)
                setCouponApplied(true)
                setTotalPriceInEuros(newPrice)
                if (newPrice <= 0) {
                    await freeOfChargeFunction()
                } else {
                    setShowPaymentMethods(true)
                }
            } else if (couponResponse && couponResponse.status === 'failed') {
                if (couponResponse.message === 'Coupon not found') setCouponErrorMessage(t('paymentPage.couponNotFound'))
                else if (couponResponse.message === 'Wrong use case') setCouponErrorMessage(t('paymentPage.couponWrongUseCase'))
                else if (couponResponse.message === 'Coupon has expired') setCouponErrorMessage(t('paymentPage.couponExpired'))
                else if (couponResponse.message === 'Coupon already used') setCouponErrorMessage(t('paymentPage.couponAlreadyUsed'))
                else setCouponErrorMessage(t('paymentPage.couponNotFound'))
                setIsLoading(false)
            }
        } catch (error) {
            console.error(error)
        }
    }

    async function freeOfChargeFunction() {
        setIsLoading(true)
        try {
            for (const p of products) await ApplicationsApi.addRights(application, p)
            const coupons = { couponCode }
            try {
                await UsersApi.updateUserUsedCoupons(userId, coupons)
                const url = new URL(window.location.href)
                url.searchParams.set('status', 'ok')
                window.location.href = url.toString()
            } catch (error) {
                console.error(error)
            }
        } catch (error) {
            console.error(error)
        }
    }

    useEffect(() => {
        getProducts()
    }, [])

    const formattedPrice = (euros: number) => {
        if (euros === 0) return '0 €'
        if (euros % 1 === 0) return `${euros},00 €`
        return euros.toFixed(2).replace('.', ',') + ' €'
    }

    return (
        <div className="flex items-center justify-center h-40 bg-gray-200 rounded-lg container-popup">
            <Grid container direction="row" justifyContent="space-evenly">
                <Grid container>
                    <Grid item xs={12}>
                        <div className="text-center">
                            <h1 style={{ margin: 0 }}>{headertext}</h1>
                            <Suggest content={infotext} containerMaxWidth='100%' contentMaxWidth='100%' isModal padding='12px 0 15px 0' />
                        </div>

                        {!isLoading ? (
                            products.length > 0 && (
                                <div>
                                    {products.map(p => (
                                        <ListItem
                                            key={p.productId}
                                            divider
                                            style={{
                                                display: 'flex',
                                                flexDirection: 'row',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                padding: '12px',
                                                width: '100%',
                                                height: '56px',
                                                backgroundColor: '#FCF3F5',
                                                borderRadius: '12px',
                                                margin: '8px 0px',
                                                boxSizing: 'border-box',
                                            }}
                                        >
                                            <Box
                                                style={{
                                                    display: 'flex',
                                                    flexDirection: 'row',
                                                    alignItems: 'center',
                                                    gap: '12px',
                                                    marginLeft: '8px',
                                                }}
                                            >
                                                <Typography
                                                    style={{ fontSize: '14px', fontWeight: 700, color: '#2F2F2F' }}
                                                >
                                                    {t('paymentPopup.selectedProduct')}
                                                </Typography>
                                                <Typography
                                                    style={{ fontSize: '14px', fontWeight: 450, color: '#2F2F2F' }}
                                                >
                                                    {t(`paymentPopup.productNames.${p.name}`)}
                                                </Typography>
                                            </Box>
                                            {/* Right side: discount + current price */}
                                            <Box style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                {['showCreditInfo', 'generateContract'].includes(p.name) && (
                                                    <Typography
                                                        style={{
                                                            fontSize: '14px',
                                                            fontWeight: 600,
                                                            color: '#6B7280',
                                                            textDecoration: 'line-through',
                                                        }}
                                                    >
                                                        {t(`paymentPopup.discountTexts.${p.name}`)}
                                                    </Typography>
                                                )}
                                                <Box
                                                    style={{
                                                        width: '81px',
                                                        height: '32px',
                                                        background: '#94004A',
                                                        borderRadius: '8px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                    }}
                                                >
                                                    <Typography
                                                        style={{
                                                            fontSize: '16px',
                                                            fontWeight: 700,
                                                            color: '#F7F8F9',
                                                            textAlign: 'center',
                                                        }}
                                                    >
                                                        {formattedPrice(p.priceInEuros)}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </ListItem>
                                    ))}
                                </div>
                            )
                        ) : (
                            <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                                <CircularProgress
                                    size={72}
                                    style={{ color: '#94004A', margin: '25px' }}
                                />
                            </div>
                        )}


                        {showPaymentMethods && paymentProviders && !isLoading && (
                            <div className="popupPaymentOptionMargins">
                                <div className="paymentOption">
                                    {paymentProviders.providers.map((provider: any) => (
                                        <form
                                            method="POST"
                                            action={provider.url}
                                            key={provider.id}
                                            className="paymentCard"
                                            onSubmit={event => {
                                                event.preventDefault()
                                                event.currentTarget.submit()
                                            }}
                                        >
                                            {provider.parameters.map((param: any) => (
                                                <input key={param.name} type="hidden" name={param.name} value={param.value} />
                                            ))}
                                            <button className="paymentProvider" style={paymentProviderWidth ? { width: paymentProviderWidth } : undefined}>
                                                <img style={{ objectFit: 'contain', width: '80%' }} src={provider.svg} alt={provider.value} />
                                            </button>
                                        </form>
                                    ))}
                                </div>
                                <p style={{ textAlign: 'center', marginTop: '2rem' }}>
                                    {t('paymentPage.paymentTermsDisclaimerTextPart')}{' '}
                                    <a style={{ color: '#94004A', textDecoration: 'none', fontWeight: 'bold' }} href="https://www.leea.fi/kayttoehdot" target="_blank" rel="noreferrer">
                                        {t('paymentPage.paymentTermsDisclaimerLinkPart')}
                                    </a>
                                </p>
                            </div>
                        )}
                        {couponSuccessMessage && (
                            <Typography variant='body1' className={`couponForm successText couponMessage`}>
                                {couponSuccessMessage}
                            </Typography>
                        )}
                        {showPaymentMethods && !paymentProviders && (
                            <p className="centeredText heading my8">{t('paymentPage.paymentProvidersNotFound')}</p>
                        )}
                    </Grid>
                </Grid>
            </Grid>
        </div>
    )
}
